/**
 * HatchEditForm — HATCH-EDIT-DIALOG (fullstack §5.1, L1 dialog-spine).
 *
 * The SECOND real per-entity form registered on the CC-3 `SpecialEditorHost`
 * (`SPECIAL_EDITOR_REGISTRY["Hatch"]`), following REVCLOUD-PROPS-EDITOR (#487).
 * AutoCAD surfaces a hatch's pattern properties behind the dedicated "Hatch Edit"
 * dialog (`HATCHEDIT`) rather than the plain Properties grid, because a pattern /
 * scale / angle / island-style edit triggers a REGEN of the derived fill instead
 * of a flat field patch. This form drives that editor's BODY; the host owns the
 * chrome (title / OK / Cancel / Apply) and the write-back → regen cycle
 * (`useSpecialEditorRegenCommit`).
 *
 * ## Read model (ПРИОРИТЕТ #1 — projects FROM the CAD core, never re-defines it)
 *
 * Every value the form shows is read through the DEFUSE S15 typed
 * `TypedPropertyDescriptor[]` the host passes in (`resolveDisplay(descriptor,
 * entity)`), NOT the string-bag `EntityPreview.properties`. The six editable
 * fields —
 *   - Pattern type       (`patternType`, Predefined / User defined / Custom),
 *   - Pattern            (`patternName`, the pattern-family name; a Pattern write
 *     routes through the `hatchPatternMutator` — it re-derives solidFill and
 *     clears gradient / customPatternLines, so the fill regenerates),
 *   - Angle              (`patternAngle`, degrees),
 *   - Scale              (`patternScale`, positive-finite — the translator rejects
 *     0 / negative),
 *   - Associative        (`associative`, whether the hatch tracks its boundary),
 *   - Hatch style        (`hatchStyle`, the island-detection style —
 *     Normal / Outer / Ignore, DXF group 75)
 * — resolve to real typed fields on `HatchEntity`.
 *
 * NOTE (documented incompleteness, ПРИОРИТЕТ #1 allowance): AutoCAD's Hatch Edit
 * dialog also exposes Double, Hatch origin and Gap tolerance. `Double`
 * (`doubleLine`, DXF 77) IS now surfaced through the property-descriptor system
 * (N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR — `hatchDescriptors` in
 * command-engine/descriptors/containers.ts, read-only unless Pattern type =
 * User defined), but only through the plain Properties palette — this
 * dedicated Hatch Edit dialog does not add a Double row yet (a separate,
 * narrower follow-up; adding it here is a pure UI-surface addition once the
 * descriptor above exists). `Hatch origin` has NO standalone
 * AcDbHatch group — it is baked into the pattern-definition line base points
 * (43/44), and Gap tolerance is the `HPGAPTOL` sysvar (a boundary-detection
 * setting, not per-entity DXF data). Neither has a per-entity typed home, so
 * fabricating a flat form field for them would violate ПРИОРИТЕТ #1; they stay
 * out of this dialog until a dedicated typed model lands.
 *
 * ## Write-back (safe contour)
 *
 * The shared `useSpecialEditorFieldDraft` controller diffs the draft against the
 * baseline and emits a single `properties/apply` carrying ONLY the changed
 * descriptor labels; the host dispatches it (validation → geometry reducer →
 * audit → undo) and records the `regen` view-op. The form NEVER mutates geometry
 * directly (CLAUDE.md "любые изменения геометрии только через Command Engine"). A
 * changed Scale that is ≤ 0 / non-finite, a non-finite Angle, or an empty Pattern
 * name ABORTS the apply (`validate` → false) so OK stays open on invalid input,
 * matching AutoCAD's reject-on-invalid.
 *
 * ## Function dependency map
 *
 * HatchEditForm (SpecialEditorFormComponent)
 *   props: { entity, descriptors, onDraftChange }
 *   useT()                        -> i18n.useT (RU+EN field labels + enum options)
 *   deriveBaseline()              -> resolveDisplay per field (typed read model)
 *   validate()                    -> Scale/Angle/Pattern reject rules
 *   useSpecialEditorFieldDraft()  -> shared draft controller (dirty + write-back)
 */

import { useCallback, useMemo } from "react";
import type { ReactElement } from "react";

import {
  resolveDisplay,
  type TypedPropertyDescriptor,
} from "@dxf-ai-editor/command-engine";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

import { useT } from "../i18n";
import type { SpecialEditorFormProps } from "./SpecialEditorHost";
import {
  SpecialEditorNumberRow,
  SpecialEditorSelectRow,
  SpecialEditorTextRow,
  type SpecialEditorSelectOption,
} from "./SpecialEditorFieldRows";
import {
  useSpecialEditorFieldDraft,
  type SpecialEditorDraftValues,
} from "./useSpecialEditorFieldDraft";

/* ────────────────────────── field ↔ descriptor labels ─────────────────────── */

/** The descriptor labels this form reads/writes. Each is a real `hatchDescriptors`
 *  entry (DEFUSE S15) whose `typedFieldRef` resolves a `HatchEntity` field, and
 *  each is in the Hatch shell-patchable set so the emitted `properties/apply`
 *  persists (`extendedPatchableLookup["Hatch"]` + the typed writer's owned set). */
const LABEL = {
  patternType: "Pattern type",
  pattern: "Pattern",
  angle: "Angle",
  scale: "Scale",
  associative: "Associative",
  hatchStyle: "Hatch style",
} as const;

/** Pattern-type options — the `HatchEntity.patternType` enum (DXF 76). Values are
 *  the descriptor's decoded LABELS (`patternTypeLabel`) so the `toRaw` inverse
 *  (`PATTERN_TYPE_CODE`) maps them back to the raw code. */
const PATTERN_TYPE_OPTIONS: ReadonlyArray<SpecialEditorSelectOption> = [
  { value: "Predefined", labelKey: "keyword.hatch.pattern_predefined" },
  { value: "User defined", labelKey: "keyword.hatch.pattern_user_defined" },
  { value: "Custom", labelKey: "keyword.hatch.pattern_custom" },
];

/** Island-detection (hatch style) options — `HatchEntity.hatchStyle` (DXF 75:
 *  0 Normal / 1 Outer / 2 Ignore). Values are the descriptor's decoded LABELS
 *  (`hatchStyleLabel`) so the `toRaw` inverse (`HATCH_STYLE_CODE`) maps them back. */
const HATCH_STYLE_OPTIONS: ReadonlyArray<SpecialEditorSelectOption> = [
  { value: "Normal", labelKey: "keyword.hatch.island_normal" },
  { value: "Outer", labelKey: "keyword.hatch.island_outer" },
  { value: "Ignore", labelKey: "keyword.hatch.island_ignore" },
];

/** Associative Yes/No options — the boolean `HatchEntity.associative` (DXF 71),
 *  whose default projection is "Yes"/"No" and whose `toRaw` accepts the yes/true/1
 *  truthy set (reuses the shared keyword.yes/keyword.no strings). */
const ASSOCIATIVE_OPTIONS: ReadonlyArray<SpecialEditorSelectOption> = [
  { value: "Yes", labelKey: "keyword.yes" },
  { value: "No", labelKey: "keyword.no" },
];

/* ──────────────────────────────── read model ─────────────────────────────── */

/** Resolve a field's current DISPLAY string through the S15 descriptor read model
 *  (`resolveDisplay` — decoded label for enums, `formatNumber` for numbers, Yes/No
 *  for booleans, verbatim for text), exactly what the Properties panel shows. */
function readFieldDisplay(
  descriptors: readonly TypedPropertyDescriptor[],
  label: string,
  entity: DrawingEntity,
): string {
  const descriptor = descriptors.find((d) => d.label === label);
  return descriptor ? resolveDisplay(descriptor, entity) : "";
}

function deriveBaseline(
  entity: DrawingEntity,
  descriptors: readonly TypedPropertyDescriptor[],
): SpecialEditorDraftValues {
  const read = (label: string): string =>
    readFieldDisplay(descriptors, label, entity);
  return {
    [LABEL.patternType]: read(LABEL.patternType),
    [LABEL.pattern]: read(LABEL.pattern),
    [LABEL.angle]: read(LABEL.angle),
    [LABEL.scale]: read(LABEL.scale),
    [LABEL.associative]: read(LABEL.associative),
    [LABEL.hatchStyle]: read(LABEL.hatchStyle),
  };
}

/** A changed Scale must be a positive finite number (AutoCAD / the HATCH Scale
 *  descriptor's `exclusiveMin: 0` rejects ≤ 0 / non-numeric). */
function isValidScale(value: string): boolean {
  const n = Number(value.trim());
  return value.trim() !== "" && Number.isFinite(n) && n > 0;
}

/** A changed Angle must be a finite number (any sign; AutoCAD accepts a negative
 *  hatch angle). Empty / non-numeric is rejected. */
function isValidAngle(value: string): boolean {
  const n = Number(value.trim());
  return value.trim() !== "" && Number.isFinite(n);
}

/* ──────────────────────────────── component ──────────────────────────────── */

export function HatchEditForm({
  entity,
  descriptors,
  onDraftChange,
}: SpecialEditorFormProps): ReactElement {
  const t = useT();

  const baseline = useMemo(
    () => deriveBaseline(entity, descriptors),
    [entity, descriptors],
  );

  // Field-specific reject rules — Scale positive-finite, Angle finite, Pattern
  // non-empty (the hatchPatternMutator drops an empty name, so aborting keeps OK
  // open rather than dispatching a no-op).
  const validate = useCallback(
    (changed: SpecialEditorDraftValues): boolean => {
      if (LABEL.scale in changed && !isValidScale(changed[LABEL.scale]!)) return false;
      if (LABEL.angle in changed && !isValidAngle(changed[LABEL.angle]!)) return false;
      if (LABEL.pattern in changed && changed[LABEL.pattern]!.trim() === "") return false;
      return true;
    },
    [],
  );

  const { draft, update } = useSpecialEditorFieldDraft(entity, baseline, onDraftChange, validate);

  return (
    <div data-testid="hatch-edit-form">
      <SpecialEditorSelectRow
        id="hatch-pattern-type"
        testId="hatch-pattern-type"
        label={t("property.pattern_type")}
        value={draft[LABEL.patternType]!}
        options={PATTERN_TYPE_OPTIONS}
        onChange={(v) => update({ [LABEL.patternType]: v })}
      />
      <SpecialEditorTextRow
        id="hatch-pattern"
        testId="hatch-pattern"
        label={t("property.pattern")}
        value={draft[LABEL.pattern]!}
        onChange={(v) => update({ [LABEL.pattern]: v })}
      />
      <SpecialEditorNumberRow
        id="hatch-angle"
        testId="hatch-angle"
        label={t("property.angle")}
        value={draft[LABEL.angle]!}
        onChange={(v) => update({ [LABEL.angle]: v })}
      />
      <SpecialEditorNumberRow
        id="hatch-scale"
        testId="hatch-scale"
        label={t("property.scale")}
        value={draft[LABEL.scale]!}
        min="0"
        onChange={(v) => update({ [LABEL.scale]: v })}
      />
      <SpecialEditorSelectRow
        id="hatch-associative"
        testId="hatch-associative"
        label={t("property.associative")}
        value={draft[LABEL.associative]!}
        options={ASSOCIATIVE_OPTIONS}
        onChange={(v) => update({ [LABEL.associative]: v })}
      />
      <SpecialEditorSelectRow
        id="hatch-style"
        testId="hatch-style"
        label={t("property.hatch_style")}
        value={draft[LABEL.hatchStyle]!}
        options={HATCH_STYLE_OPTIONS}
        onChange={(v) => update({ [LABEL.hatchStyle]: v })}
      />
    </div>
  );
}
